// Today, spread out on her desk (D_Desk `spread`): every card of the day dealt onto the dimmed left side of the room
// (her words on the right stay visible). Hover a card: its buttons (Start / Done / Stuck / Edit / Later / ✕; Later opens
// In 1 hour / After lunch / Tonight / Tomorrow). While the plan is still a draft each card has a pencil, a click
// opens it to fix it, and "Looks good" sits under them. Like D_Desk's row, more cards make every card narrower (titles
// wrap, a smaller hand), so no title hides under the next card; only past MIN_W (a long day in a small window) they
// overlap like a fanned hand.
// Also the two paper dialogs: the card flipped over to change it (D_Desk `edit`) and "When should we plan?".
import { useEffect, useRef, useState } from 'preact/hooks';
import type { CharId } from '@shared/types';
import { Icon, XTime } from '../../components';
import { EDIT, PLANTIME, ROOM, SPREAD } from './roomCopy';
import type { DeskAct } from './RoomDesk';
import s from './room.module.css';

export interface SpreadCard { id: string; label: string; title: string; foot: string; done: boolean; pencil: boolean }

interface SpreadProps {
  ch: CharId;
  vw: number;
  title: string; sub: string; foot: string;
  cards: SpreadCard[];
  actions: (c: SpreadCard, later: boolean, setLater: (on: boolean) => void) => DeskAct[];
  open: (c: SpreadCard) => void;
  confirm: (() => void) | null;
  confirmBusy?: boolean;
  close: () => void;
}

const ROT = ['-2.4deg', '1.6deg', '-1.2deg', '2.2deg', '-1.8deg', '1.2deg'];
const CARD_W = 200;
const MIN_W = 104;
/** Hover buttons need this much room for two rows (Start · Done · Stuck / Edit · Later · ✕). */
const ACTS_W = 224;

/** Card width, left of the first card and the step between cards in a row `rowW` wide, plus the type that fits. */
export function spreadLayout(n: number, rowW: number) {
  const gap = n <= 4 ? 20 : 12;
  const fit = n > 0 ? (rowW - gap * (n - 1)) / n : CARD_W;
  const w = Math.max(MIN_W, Math.min(CARD_W, fit));
  const step = n > 1 ? (fit >= MIN_W ? w + gap : (rowW - w) / (n - 1)) : 0;
  const x0 = (rowW - (w + (n - 1) * step)) / 2;
  const k = (w - MIN_W) / (CARD_W - MIN_W);
  return {
    w, step, x0,
    vars: {
      '--sw': `${w}px`, '--sfs': `${(15 + 7 * k).toFixed(1)}px`, '--spad': `${w >= 170 ? 16 : w >= 130 ? 13 : 10}px`,
      '--slab': w >= 150 ? '10.5px' : '9.5px', '--slines': w >= 170 ? '5' : '6',
    } as Record<string, string>,
  };
}

/** 'INNING 3 · 2:00 PM' wraps after the dot on a narrow card, never inside the time. */
function labelText(label: string) {
  const i = label.lastIndexOf(' · ');
  if (i < 0) return label;
  return <>{label.slice(0, i + 2)} <span class={s.nowrap}>{label.slice(i + 3)}</span></>;
}

export function RoomSpread({ ch, vw, title, sub, foot, cards, actions, open, confirm, confirmBusy, close }: SpreadProps) {
  const [hov, setHov] = useState<string | null>(null);
  const [laterFor, setLaterFor] = useState<string | null>(null);
  const rowW = Math.max(700, vw - 498); // clear of the subtitles (right 40 + 392) by 16
  const { w, step, x0, vars } = spreadLayout(cards.length, rowW);
  // The hover buttons under a narrow card: ACTS_W wide, centered on it, kept off the room's left edge and the subtitles.
  const actsAt = (left: number) => {
    const bw = Math.max(w + 24, ACTS_W);
    const al = Math.max(-42 - left, Math.min(rowW + 10 - bw - left, (w - bw) / 2));
    return { '--sal': `${al}px`, '--saw': `${bw}px` } as Record<string, string>;
  };
  const ink = { '--ink': ROOM[ch].ink } as Record<string, string>;
  const leave = (id: string) => { if (hov === id) { setHov(null); setLaterFor(null); } };
  return (
    <div class={s.spread} style={ink}>
      <button type="button" class={s.spreadBack} aria-label={SPREAD.downAria} onClick={close} style={{ width: vw - 400 }} />
      <div class={s.spreadHead} style={{ width: rowW - 40 }}>
        <h2 class={s.spreadTitle}>{title}</h2>
        <p class={s.spreadSub}>{sub}</p>
      </div>
      <div class={s.spreadRow} style={{ width: rowW, ...vars }} role="list" aria-label={title}>
        {cards.map((c, i) => {
          const on = hov === c.id;
          const acts = on ? actions(c, laterFor === c.id, (v) => setLaterFor(v ? c.id : null)) : [];
          return (
            <div
              key={c.id} class={s.sCardBox} role="listitem" data-fan
              style={{ left: x0 + i * step, zIndex: on ? 50 : i + 1, animationDelay: `${i * 90}ms`, ...actsAt(x0 + i * step) }}
              onMouseEnter={() => { if (hov !== c.id) { setHov(c.id); setLaterFor(null); } }}
              onMouseLeave={() => leave(c.id)}
            >
              <div
                class={s.sCard} role="button" tabIndex={0} data-done={c.done ? 'true' : 'false'}
                aria-label={(c.pencil ? 'Change this card: ' : c.done ? 'Done: ' : 'Card: ') + c.title}
                style={{ transform: `rotate(${ROT[i % ROT.length]}) translateY(${on ? -16 : 0}px)`, boxShadow: on ? '0 26px 44px rgba(0,0,0,0.55)' : '0 10px 24px rgba(0,0,0,0.4)' }}
                onClick={() => { if (!c.done) open(c); }}
                onFocus={() => { if (hov !== c.id) { setHov(c.id); setLaterFor(null); } }}
                onKeyDown={(e) => { if ((e.key === 'Enter' || e.key === ' ') && !c.done) { e.preventDefault(); open(c); } }}
              >
                <span class={s.paperTop} aria-hidden="true" />
                <span class={s.sLabel}>{labelText(c.label)}</span>
                <span class={s.sTitle} data-part="title">{c.title}</span>
                <span class={s.grow} />
                <span class={s.sFoot}>{c.foot}</span>
                {c.pencil && <span class={s.pencil} aria-hidden="true"><Icon name="edit" size={16} /></span>}
                {c.done && <span class={s.sStamp} aria-hidden="true">DONE</span>}
              </div>
              {acts.length > 0 && (
                <div class={s.sActs}>
                  {acts.map((a) => (
                    <button key={a.key} type="button" class={s.sAct} data-strong={a.strong ? 'true' : 'false'} aria-label={a.aria || a.label} title={a.label ? undefined : a.aria} onClick={a.run}>
                      {a.icon && <Icon name={a.icon} size={a.label ? 13 : 14} strokeWidth={2.6} fill={a.icon === 'play' ? 'currentColor' : undefined} />}
                      {a.label && <span>{a.label}</span>}
                    </button>
                  ))}
                </div>
              )}
            </div>
          );
        })}
      </div>
      <div class={s.spreadFoot} style={{ width: rowW - 40 }}>
        <p class={s.spreadFootText}>{foot}</p>
        {confirm && <button type="button" class={s.looksGood} data-cta="looks-good" aria-busy={confirmBusy ? 'true' : undefined} onClick={confirm}>Looks good</button>}
        <button type="button" class={s.putDown} onClick={close}>{SPREAD.down}</button>
      </div>
    </div>
  );
}

// ------------------------------------------------------------------ the card flipped over to change it
export interface EditValue { t: string; tm: string; mn: number }

interface EditProps {
  ch: CharId;
  vw: number;
  value: EditValue;
  draft: boolean;
  canRemove: boolean;
  onSave: (v: EditValue) => void;
  onRemove: () => void;
  onClose: () => void;
}

export function RoomEdit({ ch, vw, value, draft, canRemove, onSave, onRemove, onClose }: EditProps) {
  const [v, setV] = useState<EditValue>(value);
  const input = useRef<HTMLInputElement>(null);
  useEffect(() => { const el = input.current; if (el) { el.focus(); el.setSelectionRange(el.value.length, el.value.length); } }, []);
  const mins = EDIT.mins.includes(v.mn) ? EDIT.mins : [...EDIT.mins.slice(0, 4), v.mn].sort((a, b) => a - b);
  const R = ROOM[ch];
  const ok = v.t.trim().length > 0;
  return (
    <div class={s.dialogLayer} style={{ '--ink': R.ink } as Record<string, string>}>
      <button type="button" class={s.dialogBack} aria-label="Close" tabIndex={-1} onClick={onClose} style={{ width: vw - 400 }} />
      <form
        class={s.editCard} role="dialog" aria-modal="false" aria-label="Change this card" style={{ left: Math.max(40, Math.min(270, (vw - 400 - 440) / 2)) }}
        onSubmit={(e) => { e.preventDefault(); if (ok) onSave({ ...v, t: v.t.trim() }); }}
      >
        <p class={s.editHead}>{EDIT.head}</p>
        <input ref={input} type="text" class={s.editInput} aria-label={EDIT.what} value={v.t} maxLength={120} onInput={(e) => setV({ ...v, t: (e.currentTarget as HTMLInputElement).value })} />
        <p class={s.editCap}>{EDIT.when}</p>
        <div class={s.editWheel}><XTime value={v.tm} onPick={(tm) => setV((x) => ({ ...x, tm }))} acc="var(--acc)" bg="#2A241C" w={392} /></div>
        <p class={s.editCap}>{EDIT.long}</p>
        <div class={s.editMins} role="group" aria-label="How long">
          {mins.map((m) => (
            <button key={m} type="button" class={s.editMin} aria-pressed={v.mn === m ? 'true' : 'false'} onClick={() => setV({ ...v, mn: m })}>{`${m} min`}</button>
          ))}
        </div>
        <div class={s.editBtns}>
          <button type="submit" class={s.editSave} disabled={!ok} data-cta="save">{EDIT.save}</button>
          {canRemove && <button type="button" class={s.editRemove} onClick={onRemove}>{draft ? EDIT.takeOff : EDIT.remove}</button>}
        </div>
      </form>
    </div>
  );
}

// ------------------------------------------------------------------ "When should we plan?" (Me row, evening card)
export function RoomPlanTime({ ch, name, vw, value, onDone, onClose }: { ch: CharId; name: string; vw: number; value: string; onDone: (v: string) => void; onClose: () => void }) {
  const [v, setV] = useState(value);
  const done = useRef<HTMLButtonElement>(null);
  useEffect(() => { done.current?.focus(); }, []);
  return (
    <div class={s.dialogLayer} style={{ '--ink': ROOM[ch].ink } as Record<string, string>}>
      <button type="button" class={s.dialogBack} aria-label="Close" tabIndex={-1} onClick={onClose} style={{ width: vw }} />
      <div class={`${s.editCard} ${s.timeCard}`} role="dialog" aria-modal="false" aria-label="When should we plan?" style={{ left: (vw - 440) / 2 }}>
        <p class={s.editHead}>{PLANTIME.head}</p>
        <p class={s.timeSub}>{PLANTIME.sub.replace('{n}', name)}</p>
        <div class={s.editWheel}><XTime value={v} onPick={setV} acc="var(--acc)" bg="#2A241C" w={392} /></div>
        <div class={s.editBtns}>
          <button ref={done} type="button" class={s.editSave} data-cta="done" onClick={() => onDone(v)}>{PLANTIME.done}</button>
        </div>
      </div>
    </div>
  );
}
